import React, {KeyboardEventHandler, useCallback, useContext, useEffect, useMemo, useRef, useState,} from "react";
import {ConnectedProps} from "react-redux";
import {useNavigate, useParams} from "react-router";
import {faEnvelope, faEye, faLock, faPhone,} from "@fortawesome/pro-regular-svg-icons";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {ClavaContext} from "../../../../config/contexts";
import {translate} from "../../../../config/translator";
import {connector} from "./redux";
import {emailOrTel, isRegistered} from "../../../../config/utils";
import { normalizePhoneNumber } from "../../../../config/phone";
import LeftSide from "../LeftSide";
import ClavaTextInput from "../../../components/ClavaTextInput";
import client from "../../../../client";
import {IdentifierType} from "../../../../config/types";
import RoundedRectangle from "../../../components/RoundedRectangle";
import SegmentedTabs from "../../../components/SegmentedTabs";
import { useReCaptcha } from "../../../../hooks/useReCaptcha";
import { RECAPTCHA_ACTION_LOGIN } from "../../../../config/constants";

const Login: React.FC<ConnectedProps<typeof connector>> = ({
                                                               reset,
                                                               login,
                                                               loginStatus,
                                                               user,
                                                           }) => {
    const {l} = useContext(ClavaContext);
    const {redirectAfter} = useParams();
    const navigate = useNavigate();
    const [identifier, setIdentifier] = useState<string>("");
    const [identifierType, setIdentifierType] = useState<IdentifierType>("both");
    const [password, setPassword] = useState<string>("");
    const pwInput = useRef<HTMLInputElement>(null);
    const [passwordVisible, setPasswordVisible] = useState(false);
    const [identifierAvailable, setIdentifierAvailable] = useState(true);
    const [pwVisible, setPwVisible] = useState<boolean>(false);
    const { reCaptchaToken, refresh } = useReCaptcha(RECAPTCHA_ACTION_LOGIN);
    const mailValid = useMemo(
        () => loginStatus !== "mailGiven" && loginStatus !== "mailInvalid",
        [loginStatus]
    );
    useEffect(() => {
        reset();
    }, [reset]);
    const hasLoggedInSuccessfully = useMemo(() => {
        if (!isRegistered(user)) return false;
        const trimmedIdentifier = identifier.trim();
        if (!trimmedIdentifier) return false;
        const userEmail = user?.email?.toLowerCase();
        const identifierLower = trimmedIdentifier.toLowerCase();
        return (
            userEmail === identifierLower ||
            user?.tel === trimmedIdentifier
        );
    }, [identifier, user]);

    useEffect(() => {
        if (hasLoggedInSuccessfully) {
            setTimeout(() => {
                navigate(redirectAfter || "/home");
            }, 2000);
        }
    }, [hasLoggedInSuccessfully, navigate, redirectAfter]);
    const onLogin = useCallback(() => {
        login(identifier, password);
    }, [identifier, login, password]);

    useEffect(() => {
        if (passwordVisible && pwInput.current) {
            pwInput.current.focus();
        }
    }, [passwordVisible]);

    const onChangeEmail = useCallback((e: string) => {
        setIdentifierType(emailOrTel(e));
        setIdentifier(e);
    }, []);
    const onChangePw = useCallback((e: string) => {
        setPassword(e);
    }, []);
    const onTogglePWVisible = useCallback(() => {
        setPwVisible((v) => !v);
    }, []);
    const onKeyUpPw = useCallback<KeyboardEventHandler<HTMLInputElement>>(
        (event) => {
            if (event.key === "Enter") onLogin();
        },
        [onLogin]
    );
    const onPwForgot = useCallback(() => {
        navigate(redirectAfter ? `/pw-forgot/${redirectAfter}` : `/pw-forgot`);
    }, [navigate, redirectAfter]);
    const onGoToRegister = useCallback(() => {
        navigate(redirectAfter ? `/register/${redirectAfter}` : "/register");
    }, [navigate, redirectAfter]);
    const onLoginViaSms = useCallback(() => {
        if (identifierType === "mail") {
            if (!reCaptchaToken) {
                refresh();
                return;
            }
            client()
                .loginWithSMS({ email: identifier.toLowerCase() }, reCaptchaToken)
                .then(() => {
                    const id = encodeURIComponent(identifier.toLowerCase());
                    navigate(redirectAfter ? `/login/pending/${id}/${redirectAfter}` : `/login/pending/${id}`);
                })
                .catch((err) => {
                    if (err && err.status === 498) {
                        refresh();
                        setTimeout(() => {
                            if (!pwInput.current) {
                                client().loginWithSMS({ email: identifier.toLowerCase() }, reCaptchaToken);
                            }
                        }, 600);
                    }
                });
            return;
        }
        if (identifierType === "tel") {
            if (!reCaptchaToken) {
                refresh();
                return;
            }
            const normalizedTel = normalizePhoneNumber(identifier);
            client()
                .loginWithSMS({ tel: normalizedTel }, reCaptchaToken)
                .then(() => {
                    const id = encodeURIComponent(normalizedTel);
                    navigate(redirectAfter ? `/login/pending/${id}/${redirectAfter}` : `/login/pending/${id}`);
                })
                .catch((err) => {
                    if (err && err.status === 498) {
                        refresh();
                        setTimeout(() => {
                            if (!pwInput.current) {
                                client().loginWithSMS({ tel: normalizedTel }, reCaptchaToken);
                            }
                        }, 600);
                    }
                });
        }
    }, [identifierType, identifier, reCaptchaToken, refresh, navigate, redirectAfter]);

    const identifierCheckResult = useCallback(
        (res: boolean) => {
            setIdentifierAvailable(!res);
            if (!res) {
                setPasswordVisible(true);
                if (pwInput.current) {
                    pwInput.current.focus();
                }
            } else {
                setPasswordVisible(false);
            }
        },
        [pwInput]
    );
    const onCheckIdentifier = useCallback(() => {
        if (identifierType === "mail") {
            client().emailAvailable(identifier).then(identifierCheckResult);
        } else if (identifierType === "tel") {
            const normalizedTel = normalizePhoneNumber(identifier);
            setIdentifier(normalizedTel);
            client().telAvailable(normalizedTel).then(identifierCheckResult);
        } else {
            setIdentifierAvailable(false);
        }
    }, [identifier, identifierCheckResult, identifierType]);

    const onKeyUpMail = useCallback<KeyboardEventHandler<HTMLInputElement>>(
        (event) => {
            if (event.key === "Enter") onCheckIdentifier();
        },
        [onCheckIdentifier]
    );
    const registerInvite = (
        <div className="mt-2 mb-4 w-full max-w-96 min-w-72 text-xs text-font-600 dark:text-font-600-dark flex flex-wrap items-center justify-center text-center gap-1">
            <span>{translate("notRegisteredInvite", l)}</span>
            <button
                type="button"
                onClick={onGoToRegister}
                className="text-primary font-semibold hover:text-btn-active transition-colors"
            >
                {translate("createAccount", l)}
            </button>
        </div>
    );
    return (
        <div className="w-full px-6 xl:px-4 mx-auto xl:max-w-screen-xl">
            <div className="w-full flex flex-col items-start justify-center gap-6 xl:flex-row">
                <div className="w-full flex flex-col items-stretch justify-start xl:w-96 xl:max-w-96 xl:shrink-0">
                    <RoundedRectangle
                        hasTitle
                        titleType="upLeft"
                        titleKey="login"
                        hasBorder
                        className="bg-bg-secondary dark:bg-bg-secondary-dark border-light-gray dark:border-light-gray-dark mb-4"
                    >
                        <div className="p-6 xl:p-4">
                            {loginStatus !== "ok" && (
                                <span className="text-red font-bold">
                                    {translate(loginStatus, l)}
                                </span>
                            )}
                            {hasLoggedInSuccessfully && (
                                <span className="text-green">
                                    {translate("logInSuccess", l)}
                                </span>
                            )}
                            {!identifierAvailable && (
                                <span className="text-red font-bold">
                                    {translate("mailInvalid", l)}
                                </span>
                            )}

                            <div className="my-4 w-full max-w-96 min-w-72">
                                <ClavaTextInput
                                    onChange={onChangeEmail}
                                    value={identifier}
                                    type="text"
                                    onKeyUp={onKeyUpMail}
                                    error={!mailValid}
                                    placeholder="max@mustermann.com"
                                    label={translate(
                                        identifierType === "both"
                                            ? "mailOrTel"
                                            : identifierType === "tel"
                                                ? "telIdentifier"
                                                : "mailAddress",
                                        l
                                    )}
                                    iconL={identifierType === "tel" ? faPhone : faEnvelope}
                                />
                                {passwordVisible ? (
                                    <div className="my-4 w-full max-w-96 min-w-72">
                                        <div className="relative">
                                            <ClavaTextInput
                                                onChange={onChangePw}
                                                value={password}
                                                type={pwVisible ? "text" : "password"}
                                                error={!loginStatus}
                                                placeholder="password"
                                                iconL={faLock}
                                                onKeyUp={onKeyUpPw}
                                                innerRef={pwInput}
                                            />
                                            <button
                                                className={`w-4 h-4 bottom-3.5 right-3 absolute ${
                                                    pwVisible ? " !text-primary" : ""
                                                }`}
                                                onClick={onTogglePWVisible}
                                                type="button"
                                            >
                                                <FontAwesomeIcon icon={faEye} />
                                            </button>
                                        </div>
                                        <div className="flex my-2 flex-row justify-between w-full">
                                            <SegmentedTabs
                                                className="p-0 gap-2"
                                                size="sm"
                                                items={[
                                                    {
                                                        key: "loginViaSms",
                                                        label: translate("loginViaSms", l),
                                                        onClick: onLoginViaSms,
                                                    },
                                                ]}
                                            />
                                            <SegmentedTabs
                                                className="p-0 gap-2"
                                                size="sm"
                                                items={[
                                                    {
                                                        key: "pwForgot",
                                                        label: translate("pwForgot", l),
                                                        onClick: onPwForgot,
                                                    },
                                                ]}
                                            />
                                        </div>
                                        {password.length > 0 && (
                                            <>
                                                <div className="my-4 w-full max-w-96 min-w-72 flex justify-end">
                                                    <SegmentedTabs
                                                        className="p-0 gap-2 w-full"
                                                        fullWidth
                                                        variant="primary"
                                                        items={[{
                                                            key: "login",
                                                            label: translate("login", l),
                                                            onClick: onLogin,
                                                        }]}
                                                        activeKey="login"
                                                    />
                                                </div>
                                                {registerInvite}
                                            </>
                                        )}
                                    </div>
                                ) : (
                                    <>
                                        <div className="my-4 w-full max-w-96 min-w-72 flex justify-end">
                                            <SegmentedTabs
                                                className="p-0 gap-2 w-full"
                                                fullWidth
                                                variant="primary"
                                                items={[{
                                                    key: "continue",
                                                    label: translate("continue", l),
                                                    onClick: onCheckIdentifier,
                                                    disabled: identifier.length === 0,
                                                }]}
                                                activeKey={identifier.length > 0 ? "continue" : undefined}
                                            />
                                        </div>
                                        {registerInvite}
                                    </>
                                )}
                            </div>
                            <p className="mt-2 text-xs opacity-70 max-w-96 min-w-72">
                                {translate("recaptchaNoticeStart", l)}{" "}
                                <a
                                    href="https://policies.google.com/privacy"
                                    target="_blank"
                                    rel="noreferrer"
                                    className="underline hover:opacity-90"
                                >
                                    {translate("privacy", l)}
                                </a>
                                {translate("recaptchaNoticeAnd", l)}
                                <a
                                    href="https://policies.google.com/terms"
                                    target="_blank"
                                    rel="noreferrer"
                                    className="underline hover:opacity-90"
                                >
                                    {translate("tos", l)}
                                </a>
                                {translate("recaptchaNoticeEnd", l)}
                            </p>
                        </div>
                    </RoundedRectangle>
                </div>
                <div className="shrink-0 w-96 max-w-96 hidden xl:block">
                    <LeftSide/>
                </div>
            </div>
        </div>
    );
};

export default connector(Login);
